/* eslint-disable max-len */
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Time, TimeSpan } from '@moderepo/mode-app-components';
import { TimeSeriesAggregation } from '@moderepo/mode-internal-apis';
import { modeUIContext, showInfoNotification } from '@moderepo/mode-ui-state';
import moment from 'moment';
import { useCallback, useContext, useMemo } from 'react';
import { PredefinedTimeRangeType } from '..';
import { SettingsErrorLogger } from '../../utils/logger';
import { isObjectLiteral, isValidSettingNumberValue, isValidSettingStringValue } from '../../utils/utils';
import { TimeRangeControlBar } from './TimeRangeControlBar';



/**
 * The type of time controller a view can use
 */
export enum TimeControlType {
    TIME_SPAN = 'Time_Span',
    TIME_RANGE = 'Time_Range',
    FIXED_TIME_SPAN = 'Fixed_Time_Span',
    FIXED_TIME_RANGE = 'Fixed_Time_Range'
}


export const SUPPORT_TIME_SPANS = [TimeSpan.DAY, TimeSpan.WEEK, TimeSpan.MONTH];


/**
 * Interface to customize Time Span control. This allow us to tell the entity details page to not only use the 'Time_Span' control but also
 * configure the control based on these settings.
 * For now, there is nothing we need to be able to customize for the Time_Span control. Will add them later if needed.
 */
export interface EntityDetailsTimeSpanControlSettings {
    readonly type: TimeControlType.TIME_SPAN;

    // The list of timeSpans the user is allowed to use. By default, we will show all options. However, sometimes we want to disable some options
    // and this attribute is to limit the number of options.
    readonly enabledTimeSpans?: readonly [TimeSpan, ...TimeSpan[]] | undefined;

    // The initial timeSpan to use instead of the default, 1 Day. For example, to have the app initialize the timeSpan control to use
    // 1 Week then set this attribute to 'week'. NOTE: if "timeSpans" is configured then this "defaultTimeSpan" must be in the list of
    // timeSpans.
    readonly defaultTimeSpan?: TimeSpan | undefined;

    // To Add N timeSpans from the current date/time and use that as the default instead of the current date/time.
    // Sometimes we want to show data from 1 timeSpan BEFORE the current date/time instead of the current date/time. This is usually used by the
    // Reports view which show KPI metrics. Because KPI values are not available until the END of a timeSpan therefore, if we initialize the control
    // show data for the current date/time, the UI might not show any data. Therefore, for Reports view, we will set this value to -1 to
    // have the view show data from 1 timeSpan before the current date/time.
    readonly defaultTimeSpanOffset?: number | undefined;

    // The minimum number of timestamp inputs we want to show initially
    readonly minTimestampInputs?: number | undefined;

    // The limit number of timestamp the user can select. This is used for "Compare To" option. The default is 2. Set it to higher number to allow
    // more comparison. If we don't want to use this control for comparing data, set it to 1.
    readonly maxTimestampInputs?: number | undefined;
}


/**
 * Type guard function to check if an object is a EntityDetailsTimeSpanControlSettings
 */
export const isEntityDetailsTimeSpanControlSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityDetailsTimeSpanControlSettings => {
    const settings = obj as EntityDetailsTimeSpanControlSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }


    if (settings.type !== TimeControlType.TIME_SPAN) {
        return errorLogger?.logError({
            message: `"type" is required and must be "${TimeControlType.TIME_SPAN}".`,
            value  : settings.type,
            path   : [...configPath, 'type'],
        }) ?? false;
    }


    // enabledTimeSpans is optional but if it is provided, it must be one of the supportedTimeSpan
    if (settings.enabledTimeSpans !== undefined) {
        if (!(settings.enabledTimeSpans instanceof Array) || settings.enabledTimeSpans.length === 0) {
            return errorLogger?.logError({
                message: `"enabledTimeSpans" must be undefined or a non-empty array of these values [${
                    SUPPORT_TIME_SPANS.map((timeSpan) => {
                        return `"${timeSpan}"`;
                    }).join(', ')}].`,
                value: settings.enabledTimeSpans,
                path : [...configPath, 'enabledTimeSpans'],
            }) ?? false;
        }

        if (!settings.enabledTimeSpans.every((timeSpan, index) => {
            if (!isValidSettingStringValue(timeSpan, false, false, SUPPORT_TIME_SPANS)) {
                return errorLogger?.logError({
                    message: `Every values in "enabledTimeSpans" array must be one of the following values [${
                        SUPPORT_TIME_SPANS.map((timeSpan) => {
                            return `"${timeSpan}"`;
                        }).join(', ')}].`,
                    value: timeSpan,
                    path : [...configPath, 'enabledTimeSpans', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    if (!isValidSettingStringValue(settings.defaultTimeSpan, false, false, SUPPORT_TIME_SPANS)) {
        return errorLogger?.logError({
            message: `"defaultTimeSpan" must be undefined or one of the following values [${
                SUPPORT_TIME_SPANS.map((timeSpan) => {
                    return `"${timeSpan}"`;
                }).join(', ')}].`,
            value: settings.defaultTimeSpan,
            path : [...configPath, 'defaultTimeSpan'],
        }) ?? false;
    }

    if (settings.enabledTimeSpans && settings.defaultTimeSpan && !settings.enabledTimeSpans.includes(settings.defaultTimeSpan)) {
        return errorLogger?.logError({
            message: `"defaultTimeSpan" must be one of the time spans specified in "enabledTimeSpans" array [${
                settings.enabledTimeSpans.map((timeSpan) => {
                    return `"${timeSpan}"`;
                }).join(', ')}].`,
            value: settings.defaultTimeSpan,
            path : [...configPath, 'defaultTimeSpan'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.defaultTimeSpanOffset, false, false, -100, 100)) {
        return errorLogger?.logError({
            message: '"defaultTimeSpanOffset" must be undefined or a number between -100 and 100.',
            value  : settings.defaultTimeSpanOffset,
            path   : [...configPath, 'defaultTimeSpanOffset'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.minTimestampInputs, false, false, 1, 5)) {
        return errorLogger?.logError({
            message: '"minTimestampInputs" must be undefined or a number between 1 and 5.',
            value  : settings.minTimestampInputs,
            path   : [...configPath, 'minTimestampInputs'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.maxTimestampInputs, false, false, 1, 5)) {
        return errorLogger?.logError({
            message: '"maxTimestampInputs" must be undefined or a number between 1 and 5.',
            value  : settings.maxTimestampInputs,
            path   : [...configPath, 'maxTimestampInputs'],
        }) ?? false;
    }

    // Make sure minTimestampInputs <= maxTimestampInputs
    if (settings.minTimestampInputs !== undefined && settings.maxTimestampInputs !== undefined
        && settings.minTimestampInputs > settings.maxTimestampInputs) {
        return errorLogger?.logError({
            message: '"maxTimestampInputs" must be undefined or greater than or equal "minTimestampInputs".',
            value  : settings.maxTimestampInputs,
            path   : [...configPath, 'maxTimestampInputs'],
        }) ?? false;
    }

    return true;
};


/**
 * The time range control can have these 3 behavior. By default, the user can adjust the start and end date independently. However, we can
 * customize the time control bar to have these 2 behaviors
 * - START_ANCHORED - Means the start date will be used as the anchor. Changing the start date will automatically move the end
 *      date so that the range is within the limit. The user can then change the end date to decrease the range.
 * - END_ANCHORED - Means the opposite. The end date will be used as the anchor. The user can then adjust the start date to
 *      decrease the limit.
 */
export enum TimeRangeControlBehavior {
    START_ANCHORED = 'start_anchored',
    END_ANCHORED = 'end_anchored',
}


/**
 * The different ways the Time Range control can initialize it's start/end time. If "timeBase" is set to START_FROM_BEGINNING_OF_XXX, it means
 * we will initialize the time range's startTime to the beginning of the time unit (hour, day, week, month) and the endTime will be some # of
 * hours AFTER the startTime. If "timeBase" is set to END_AT_BEGINNING_OF_XXX then we will initialize the time range's endTime to the beginning
 * of the time unit (hour, day, week, month) and then set the startTime to be some # of hours BEFORE the endTime.
 */
export enum TimeRangeBase {
    START_FROM_BEGINNING_OF_HOUR = 'start_from_beginning_of_hour',
    START_FROM_BEGINNING_OF_DAY = 'start_from_beginning_of_day',
    START_FROM_BEGINNING_OF_WEEK = 'start_from_beginning_of_week',
    START_FROM_BEGINNING_OF_MONTH = 'start_from_beginning_of_month',

    END_AT_BEGINNING_OF_HOUR = 'end_at_beginning_of_hour',
    END_AT_BEGINNING_OF_DAY = 'end_at_beginning_of_day',
    END_AT_BEGINNING_OF_WEEK = 'end_at_beginning_of_week',
    END_AT_BEGINNING_OF_MONTH = 'end_at_beginning_of_month',
}


/**
 * Interface to customize Time Range control. This allow us to tell the entity details page to not only use the 'Time_Range' control but also
 * configure the control based on these settings.
 */
export interface TimeRangeControlSettings {
    readonly type: TimeControlType.TIME_RANGE;

    // To specify what time the initial time range should be based on. The default will use NOW as the endTime and set the startTime to some # of
    // hours BEFORE the endTime. If we want the startTime or endTime to be based on the current HOUR, DAY, WEEK, or MONTH, we can set this
    // attribute to one of the TimeRangeBase values
    readonly timeBase?: TimeRangeBase | undefined;

    // How many time unit to add to the initial startTime. This setting goes with the "timeBase" and it allows the user to adjust the start time.
    // For example, if the user wants to start from the beginning of YESTERDAY, they can set "timeBase" to "start_from_beginning_of_day"
    // and timeBaseOffset to -1. To have the time range start from last week, set "timeBase" to "start_from_beginning_of_week" and "timeBaseOffset"
    // to -1.
    readonly timeBaseOffset?: number | undefined;

    // Maximum range between start/end time in hours. We probably don't need to support smaller range than 1 hour.
    // The allowed values are any number between 1 hour and 1 year
    readonly maxRangeInHours?: number | undefined;

    // To customize the default range between start/end time in hours. By default, we will use 1 day unless this is specified.
    // The allowed values are any number between 1 hour and 30 days. Also, defaultRangeInHours can't be larger than maxRangeInHours if
    // maxRangeInHours is specified.
    readonly defaultRangeInHours?: number | undefined;

    // How much to shift the start/end time when the user move prev/next time range. By default, the amount we shift the start/end is based
    // on the range between the start/end. So if the range between start/end is 10 hours, each time the user shift prev or next, we will
    // shift the start/end by +-10 hours.
    // To change the default behavior to shift the start/end by a fixed amount, set a value for this attribute. The allowed values are between
    // 1 hour and 1 year.
    readonly timeShiftInHours?: number | undefined;

    // To customize the behavior of the time control
    readonly behavior?: TimeRangeControlBehavior | undefined;
}


/**
 * Type guard function to check if an object is a TimeRangeControlSettings
 */
export const isTimeRangeControlSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is TimeRangeControlSettings => {
    const settings = obj as TimeRangeControlSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (settings.type !== TimeControlType.TIME_RANGE) {
        return errorLogger?.logError({
            message: `"type" is required and must be "${TimeControlType.TIME_RANGE}".`,
            value  : settings.type,
            path   : [...configPath, 'type'],
        }) ?? false;
    }

    // timeBase is optional but if it is provided, it must be one of the TimeRangeBase
    const timeBase = Object.values(TimeRangeBase);
    if (!isValidSettingStringValue(settings.timeBase, false, false, timeBase)) {
        return errorLogger?.logError({
            message: `"timeBase" must be undefined or one of the following values [${
                timeBase.map((type) => {
                    return `"${type}"`;
                }).join(', ')}]`,
            value: settings.timeBase,
            path : [...configPath, 'timeBase'],
        }) ?? false;
    }

    // timeBaseOffset can be undefined or a number from -100 to 100
    if (!isValidSettingNumberValue(settings.timeBaseOffset, false, false, -100, 100)) {
        return errorLogger?.logError({
            message: '"timeBaseOffset" must be undefined or an integer between -100 and 100.',
            value  : settings.timeBaseOffset,
            path   : [...configPath, 'timeBaseOffset'],
        }) ?? false;
    }

    // maxRangeInHours can be undefined or a number from 1 day to 1 year
    if (!isValidSettingNumberValue(settings.maxRangeInHours, false, false, 1, 365 * 24)) {
        return errorLogger?.logError({
            message: `"maxRangeInHours" must be undefined or an integer between ${1} and ${365 * 24}.`,
            value  : settings.maxRangeInHours,
            path   : [...configPath, 'maxRangeInHours'],
        }) ?? false;
    }

    // defaultRangeInHours can be undefined or a number from 1 day to 1 year
    if (!isValidSettingNumberValue(settings.defaultRangeInHours, false, false, 1, 365 * 24)) {
        return errorLogger?.logError({
            message: `"defaultRangeInHours" must be undefined or an integer between ${1} and ${365 * 24}.`,
            value  : settings.defaultRangeInHours,
            path   : [...configPath, 'defaultRangeInHours'],
        }) ?? false;
    }

    // make sure defaultRangeInHours is less than or equal to maxRangeInHours
    if (settings.maxRangeInHours !== undefined && settings.defaultRangeInHours !== undefined && settings.defaultRangeInHours > settings.maxRangeInHours) {
        return errorLogger?.logError({
            message: '"defaultRangeInHours" must be less than or equal "maxRangeInHours".',
            value  : settings.defaultRangeInHours,
            path   : [...configPath, 'defaultRangeInHours'],
        }) ?? false;
    }

    // timeShiftInHours can be undefined or a number from 1 day to 1 year
    if (!isValidSettingNumberValue(settings.timeShiftInHours, false, false, 1, 365 * 24)) {
        return errorLogger?.logError({
            message: `"timeShiftInHours" must be undefined or an integer between ${1} and ${365 * 24}.`,
            value  : settings.timeShiftInHours,
            path   : [...configPath, 'timeShiftInHours'],
        }) ?? false;
    }

    // behavior can be undefined or one of the values defined in the TimeRangeControlBehavior enum
    if (!isValidSettingStringValue(settings.behavior, false, false, Object.values(TimeRangeControlBehavior))) {
        return errorLogger?.logError({
            message: `"behavior" must be undefined or one of the following values [${
                Object.values(TimeRangeControlBehavior).map((type) => {
                    return `"${type}"`;
                }).join(', ')}]`,
            value: settings.behavior,
            path : [...configPath, 'behavior'],
        }) ?? false;
    }

    return true;
};


/**
 * To specify a SPECIFIC time span to use instead of getting the time from the timeControl UI. This can be used for views that don't
 * have time control and we want the view to have all EntityUIComponent use a specific time span.
 */
export interface FixedTimeSpanControlSettings {
    readonly type: TimeControlType.FIXED_TIME_SPAN;

    // One of the predefined time span types. The default is TimeSpan.DAY.
    readonly timeSpan?: TimeSpan | undefined;

    // To Add N timeSpans from the current date/time and use that as the default instead of the current date/time.
    // Sometimes we want to show data from 1 timeSpan BEFORE the current date/time instead of the current date/time. This is usually used by the
    // Reports view which show KPI metrics. Because KPI values are not available until the END of a timeSpan therefore, if we initialize the control
    // show data for the current date/time, the UI might not show any data. Therefore, for Reports view, we will set this value to -1 to
    // have the view show data from 1 timeSpan before the current date/time.
    readonly defaultTimeSpanOffset?: number | undefined;
}


/**
 * Type guard function to make sure a setting object is a FixedTimeSpanControlSettings
 */
export const isFixedTimeSpanControlSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is FixedTimeSpanControlSettings => {
    const settings = obj as FixedTimeSpanControlSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (settings.type !== TimeControlType.FIXED_TIME_SPAN) {
        return errorLogger?.logError({
            message: `"type" must be "${TimeControlType.FIXED_TIME_SPAN}".`,
            value  : settings.type,
            path   : [...configPath, 'type'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.timeSpan, false, false, SUPPORT_TIME_SPANS)) {
        return errorLogger?.logError({
            message: `"timeSpan" must be undefined or one of the following values [${
                SUPPORT_TIME_SPANS.map((timeSpan) => {
                    return `"${timeSpan}"`;
                }).join(', ')}].`,
            value: settings.timeSpan,
            path : [...configPath, 'timeSpan'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.defaultTimeSpanOffset, false, false, -100, 100)) {
        return errorLogger?.logError({
            message: '"defaultTimeSpanOffset" must be undefined or an integer between -100 and 100.',
            value  : settings.defaultTimeSpanOffset,
            path   : [...configPath, 'defaultTimeSpanOffset'],
        }) ?? false;
    }

    return true;
};



/**
 * To specify a SPECIFIC time range to use instead of getting the time from the timeControl UI. This can be used for views that don't
 * have time control and we want the view to have all EntityUIComponent use a specific time range.
 */
export interface FixedTimeRangeControlSettings {
    readonly type: TimeControlType.FIXED_TIME_RANGE;

    // One of the predefined time range types. The default is CURRENT_DAY.
    readonly timeRangeType?: PredefinedTimeRangeType | undefined;

    /**
     * Different company has different start value for some of the time range type. For example, Some company's first day of the week is Sunday
     * and for some it's Monday. Some company's first month of the year is January but it's April for others. Therefore, the admin can also specify
     * what the starting value is using this attribute. This is OPTIONAL and the default is 0 which mean the first day of week is Sunday,
     * first month of the year is January, first day of the month is the 1st, etc... The admin only need to set a value for this attribute
     * if they want to use a different value as starting value. For example: To make Monday as the first day of week, set startValue to 1.
     * To make April as first month of the year, set startValue to 3, etc...
     */
    readonly startValue?: number | undefined;
}


/**
 * Type guard function to make sure a setting object is a FixedTimeRangeControlSettings
 */
export const isFixedTimeRangeControlSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is FixedTimeRangeControlSettings => {
    const settings = obj as FixedTimeRangeControlSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (settings.type !== TimeControlType.FIXED_TIME_RANGE) {
        return errorLogger?.logError({
            message: `"type" must be "${TimeControlType.FIXED_TIME_RANGE}".`,
            value  : settings.type,
            path   : [...configPath, 'type'],
        }) ?? false;
    }

    // timeRangeType is optional but if it is provided, it must be one of the PredefinedTimeRangeType
    const timeRangeTypes = Object.values(PredefinedTimeRangeType);
    if (!isValidSettingStringValue(settings.timeRangeType, false, false, timeRangeTypes)) {
        return errorLogger?.logError({
            message: `"timeRangeType" must be undefined or one of the following values [${
                timeRangeTypes.map((type) => {
                    return `"${type}"`;
                }).join(', ')}]`,
            value: settings.timeRangeType,
            path : [...configPath, 'timeRangeType'],
        }) ?? false;
    }

    // startValue is options but if it is provided, it must be between 1 and 60.
    if (!isValidSettingNumberValue(settings.startValue, false, false, 1, 60)) {
        return errorLogger?.logError({
            message: '"startValue" must be undefined or an integer between 1 and 60.',
            value  : settings.startValue,
            path   : [...configPath, 'startValue'],
        }) ?? false;
    }

    return true;
};



/**
 * Given a startTime, automatically adjust the endTime so that the range between the start/end time is equal the maxRangeInHours
 */
const getAdjustedEndTimeBasedOnAnchoredStartTime = (
    startTime: MaterialUiPickersDate,
    maxRangeInHours: number,
): number | undefined => {

    const hasStartTimeValue = startTime && !Number.isNaN(startTime.valueOf());
    const startTimeValue = hasStartTimeValue ? startTime.valueOf() : undefined;

    // If newStartTime is invalid then don't need to adjust anything
    if (startTimeValue === undefined) {
        return undefined;
    }

    return moment(startTimeValue).add(maxRangeInHours, 'hours').valueOf();
};



/**
 * Given an endTime, automatically adjust the startTime so that the range between the start/end time is equal the maxRangeInHours
 */
const getAdjustedStartTimeBasedOnAnchoredEndTime = (
    endTime: MaterialUiPickersDate,
    maxRangeInHours: number,
): number | undefined => {

    const hasEndTimeValue = endTime && !Number.isNaN(endTime.valueOf());
    const endTimeValue = hasEndTimeValue ? endTime.valueOf() : undefined;

    // If newEndTime is invalid then don't need to adjust anything
    if (endTimeValue === undefined) {
        return undefined;
    }

    return moment(endTimeValue).subtract(maxRangeInHours, 'hours').valueOf();
};


/**
 * Given the newStartTime, check if the newStartTime would cause any issue with the range. newStartTime must be BEFORE prevEndTime and
 * the range between newStartTime/prevEnd time must be within the max range if max range is specified.
 * If newStartTime time causes problem with the range, automatically adjust the prevEndTime to make the range is valid and return the adjusted
 * endTime. Return undefined if newStartTime doesn't cause any problem with the range.
 */
const getAdjustedEndTime = (
    prevStartTime: MaterialUiPickersDate,
    prevEndTime: MaterialUiPickersDate,
    newStartTime: MaterialUiPickersDate,
    timeControlSettings: TimeRangeControlSettings,
): number | undefined => {

    const hasPrevStartTimeValue = prevStartTime && !Number.isNaN(prevStartTime.valueOf());
    const hasPrevEndTimeValue = prevEndTime && !Number.isNaN(prevEndTime.valueOf());
    const hasNewStartTimeValue = newStartTime && !Number.isNaN(newStartTime.valueOf());

    const prevStartTimeValue = hasPrevStartTimeValue ? prevStartTime.valueOf() : undefined;
    const newStartTimeValue = hasNewStartTimeValue ? newStartTime.valueOf() : undefined;
    const prevEndTimeValue = hasPrevEndTimeValue ? prevEndTime.valueOf() : undefined;

    // If newStartTimeValue or prevEndTimeValue are invalid then don't need to adjust anything
    if (newStartTimeValue === undefined || prevEndTimeValue === undefined) {
        return undefined;
    }

    if (newStartTimeValue >= prevEndTimeValue) {
        // newStartTime is AFTER prevEndTime therefore we need to adjust the endTime. We will shift the prevEndTime so that the range stay
        // the same as the previous range. The previous range is the range between the prevStartTime and prevEndTime
        const timeRangeInHours = prevStartTimeValue
            ? (prevEndTimeValue - prevStartTimeValue) / Time.HOUR_IN_MS
            : timeControlSettings.defaultRangeInHours ?? 1;
        return moment(newStartTimeValue).add(timeRangeInHours, 'hours').valueOf();
    }

    // if maxRangeInHours is specified and the range between newStartTime and prevEndTime is GREATER than maxRangeInHours then adjust the prevEndTime
    // so that the range between newStartTime/prevEndTime time is equal maxRangeInHours
    const timeRangeInHours = (prevEndTimeValue - newStartTimeValue) / Time.HOUR_IN_MS;
    if (timeControlSettings.maxRangeInHours !== undefined && timeRangeInHours > timeControlSettings.maxRangeInHours) {
        return moment(newStartTimeValue).add(timeControlSettings.maxRangeInHours, 'hours').valueOf();
    }

    return undefined;
};



/**
 * Given the newEndTime, check if the newEndTime would cause any issue with the range. newEndTime must be AFTER prevStartTime and
 * the range between newEndTime/prevStartTime time must be within the max range if max range is specified.
 * If newEndTime time causes problem with the range, automatically adjust the prevStartTime to make the range is valid and return the adjusted
 * startTime. Return undefined if newEndTime doesn't cause any problem with the range.
 */
const getAdjustedStartTime = (
    prevStartTime: MaterialUiPickersDate,
    prevEndTime: MaterialUiPickersDate,
    newEndTime: MaterialUiPickersDate,
    timeControlSettings: TimeRangeControlSettings,
): number | undefined => {

    const hasPrevStartTimeValue = prevStartTime && !Number.isNaN(prevStartTime.valueOf());
    const hasPrevEndTimeValue = prevEndTime && !Number.isNaN(prevEndTime.valueOf());
    const hasNewEndTimeValue = newEndTime && !Number.isNaN(newEndTime.valueOf());

    const prevStartTimeValue = hasPrevStartTimeValue ? prevStartTime.valueOf() : undefined;
    const prevEndTimeValue = hasPrevEndTimeValue ? prevEndTime.valueOf() : undefined;
    const newEndTimeValue = hasNewEndTimeValue ? newEndTime.valueOf() : undefined;

    // If prevStartTime or newEndTime are invalid then don't adjust anything
    if (prevStartTimeValue === undefined || newEndTimeValue === undefined) {
        return undefined;
    }

    if (prevStartTimeValue >= newEndTimeValue) {
        // newEndTime is BEFORE prevStartTime therefore we need to adjust the prevStartTime. We will shift the prevStartTime so that the
        // range stays the same as before. The previous range is the different between prevStartTime and prevEndTime
        const timeRangeInHours = prevEndTimeValue
            ? (prevEndTimeValue - prevStartTimeValue) / Time.HOUR_IN_MS
            : timeControlSettings.defaultRangeInHours ?? 1;
        return moment(newEndTimeValue).subtract(timeRangeInHours, 'hours').valueOf();
    }

    // if maxRangeInHours is specified and the range between start and end time is GREATER than maxRangeInHours then adjust the endTime
    // so that the range between start/end time is equal maxRangeInHours
    const timeRangeInHours = (newEndTimeValue - prevStartTimeValue) / Time.HOUR_IN_MS;
    if (timeControlSettings.maxRangeInHours !== undefined && timeRangeInHours > timeControlSettings.maxRangeInHours) {
        return moment(newEndTimeValue).subtract(timeControlSettings.maxRangeInHours, 'hours').valueOf();
    }

    return undefined;
};



export interface TimeRangeControlBarContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly timeControlSettings: TimeRangeControlSettings;
    readonly startTime: number;
    readonly endTime: number;
    readonly aggregation?: TimeSeriesAggregation | undefined;
    readonly onInputsChanged: (startTime: number, endTime: number)=> void;
}


/**
 * This container is a wrapper for the EntityDetailsTimeRangeControlBar. It contains the logic for the EntityDetailsTimeRangeControlBar
 * component.
 */
export const TimeRangeControlBarContainer: React.FC<TimeRangeControlBarContainerProps> = (
    {
        className, projectId, timeControlSettings, startTime, endTime, onInputsChanged,
    }: TimeRangeControlBarContainerProps,
) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);


    /**
     * On startTime changed by user, replace the startTime. Also, re-validate the start and end time to make sure the satisfy the settings
     */
    const onStartTimeChanged = useCallback((newStartTime: MaterialUiPickersDate) => {
        if (newStartTime && !Number.isNaN(newStartTime.valueOf())) {
            let newEndTime = endTime;

            if (timeControlSettings.behavior === TimeRangeControlBehavior.START_ANCHORED && timeControlSettings.maxRangeInHours) {
                // If the behavior of the time control bar is START_ANCHORED, we will automatically update the endTime so that the
                // range is equal timeControlSettings.maxRangeInHours
                const adjustedEndTime = getAdjustedEndTimeBasedOnAnchoredStartTime(newStartTime, timeControlSettings.maxRangeInHours);
                if (adjustedEndTime) {
                    newEndTime = adjustedEndTime;
                }
            } else {
                // It is possible that the newStartTime causes the range to go outside of the limit or the newStartTime is GREATER than
                // endTime therefore we need to call "getAdjustedEndTime" to check if we need to adjust the endTime accordingly to keep
                // the range within the limit.
                const adjustedEndTime = getAdjustedEndTime(moment(startTime), moment(endTime), newStartTime, timeControlSettings);
                if (adjustedEndTime) {
                    newEndTime = adjustedEndTime;
                    uiDispatch(showInfoNotification('notif.time_control.invalid_range_fixed_message'));
                }
            }

            onInputsChanged(newStartTime.valueOf(), newEndTime);

        }
    }, [endTime, onInputsChanged, startTime, timeControlSettings, uiDispatch]);


    /**
     * On endTime changed by user, replace the endTime. Also, re-validate the start and end time to make sure the satisfy the settings
     */
    const onEndTimeChanged = useCallback((newEndTime: MaterialUiPickersDate) => {
        if (newEndTime && !Number.isNaN(newEndTime.valueOf())) {
            let newStartTime = startTime;

            if (timeControlSettings.behavior === TimeRangeControlBehavior.END_ANCHORED && timeControlSettings.maxRangeInHours) {
                // If the behavior of the time control bar is END_ANCHORED, we will automatically update the startTime so that the
                // range is equal timeControlSettings.maxRangeInHours
                const adjustedStartTime = getAdjustedStartTimeBasedOnAnchoredEndTime(moment(newEndTime), timeControlSettings.maxRangeInHours);
                if (adjustedStartTime) {
                    newStartTime = adjustedStartTime;
                }
            } else {
                // It is possible that the newEndTime causes the range to go outside of the limit or the newEndTime is LESS THAN than
                // startTime therefore we need to call "getAdjustedStartTime" to check if we need to adjust the startTime accordingly to keep
                // the range within the limit.
                const adjustedStartTime = getAdjustedStartTime(moment(startTime), moment(endTime), newEndTime, timeControlSettings);
                if (adjustedStartTime) {
                    newStartTime = adjustedStartTime;
                    uiDispatch(showInfoNotification('notif.time_control.invalid_range_fixed_message'));
                }
            }

            onInputsChanged(newStartTime, newEndTime.valueOf());
        }

    }, [endTime, onInputsChanged, startTime, timeControlSettings, uiDispatch]);


    /**
     * On Prev time range button clicked, automatically move the time range's start and end time 1 range back
     */
    const onPrevButtonClicked = useCallback(() => {
        // Use the range from 'timeControlSettings.timeShiftInHours' if it is provided. If not, use range between start/end time
        const rangeInMS = timeControlSettings.timeShiftInHours !== undefined
            ? timeControlSettings.timeShiftInHours * Time.HOUR_IN_MS
            : Math.abs(endTime.valueOf() - startTime.valueOf());

        // Move the start/end time 1 range back
        onInputsChanged(startTime.valueOf() - rangeInMS, endTime.valueOf() - rangeInMS);

    }, [endTime, onInputsChanged, startTime, timeControlSettings.timeShiftInHours]);


    /**
     * On Prev time range button clicked, automatically move the time range's start and end time 1 range back
     */
    const onNextButtonClicked = useCallback(() => {
        // Use the range from 'timeControlSettings.timeShiftInHours' if it is provided. If not, use range between start/end time
        const rangeInMS = timeControlSettings.timeShiftInHours !== undefined
            ? timeControlSettings.timeShiftInHours * Time.HOUR_IN_MS
            : Math.abs(endTime.valueOf() - startTime.valueOf());

        // Move the start/end time 1 range back
        onInputsChanged(startTime.valueOf() + rangeInMS, endTime.valueOf() + rangeInMS);
    }, [endTime, onInputsChanged, startTime, timeControlSettings.timeShiftInHours]);



    /**
     * For the next button, we don't want the user to go into the future since there is no data anyway, we will only enable the
     * button if endTime <= NOW
     */
    const canEnableNextButton = useMemo(() => {
        return Boolean(endTime.valueOf() <= Date.now());
    }, [endTime]);


    return (
        <TimeRangeControlBar
            className={className}
            projectId={projectId}
            startTime={startTime}
            endTime={endTime}
            enablePrevButton
            enableNextButton={canEnableNextButton}
            onStartTimeChanged={onStartTimeChanged}
            onEndTimeChanged={onEndTimeChanged}
            onPrevButtonClicked={onPrevButtonClicked}
            onNextButtonClicked={onNextButtonClicked}
        />
    );
};
